import { get } from 'lodash';
import { AppAction, Application } from '../../apis/eols-app-link/eols-app-link.constants';
import { AssignmentDto, AssignmentType } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import { isInstructor, stripNilProps } from '../../utilities/common.utilities';
import { RecContentItemTypeDto } from '../../apis/rec-gateway/rec-gateway.dtos';
import { RoutePath } from '../../components/app/app.constants';
import {
  ResourceCategory,
  SyllabusItemAction,
  SyllabusItemConfigType,
  SyllabusItemTypeConfig
} from '../../constants/content-type.constants';
import {
  ApplicationNavigateHandlerMapProps,
  AssignmentTypeStrategy,
  CoursePlanClickSettings,
  CoursePlanPerformanceReportClickProps,
  CoursePlanTitleClickProps,
  EntityProps,
  SortCatalogItems
} from '../model';
import {
  CatalogAdaptiveLessonExternalEntityDto
} from '../../apis/sherpath-course-management-service/sherpath-course-management-service.dtos';
import {
  getCatalogItemFromSyllabusItem,
  getEnhancedLesson,
  getEnhancedLessonClone
} from '../../utilities/assignment-types-shared.utilities';
import { navigateExternal } from '../utils/common.utils';
import {
  CatalogPageTitleClickProps,
  GetAssignmentInfoProps,
  RecContentItemTitleHandlerProps,
  RecContentItemTitleHandlerPropsClone,
} from '../../models/assignment-types-shared.models';

export const adaptiveLesson: AssignmentTypeStrategy = {
  coursePlanTitleClick: (props: CoursePlanTitleClickProps) => {
    const { defArgs, sharedAppLinkParams } = props;
    const { userRole, navigateToApp } = defArgs.props;
    const action = isInstructor(userRole) ? AppAction.ASSIGNMENT_PREVIEW : AppAction.ASSESSMENT_START;
    return Promise.resolve(
      navigateToApp({
        app: Application.KNOWLEDGECARD,
        action,
        body: sharedAppLinkParams
      })
    );
  },
  coursePlanClickSettings: (props: CoursePlanClickSettings) => {
    const { assignment, defArgs, syllabusItem, sharedAppLinkParams, isDisableLinkToAMA } = props;
    const { catalog, navigateToApp } = defArgs.props;
    const assignmentEditorApp = isDisableLinkToAMA ? Application.KNOWLEDGECARD : Application.ASSIGNMENT_MANAGEMENT;
    const action = assignment ? AppAction.ASSIGNMENT_EDIT : AppAction.ASSIGNMENT_CREATE;
    const updatedAppLinkParams = stripNilProps({
      catalogItem: getCatalogItemFromSyllabusItem(catalog, syllabusItem),
      title: get(assignment, 'title', null) || syllabusItem.title,
      availableDate: get(assignment, 'availableDate', null),
    });
    const body = {
      ...sharedAppLinkParams,
      ...updatedAppLinkParams
    };

    navigateToApp({
      app: assignmentEditorApp,
      action,
      body
    });
  },
  coursePlanPerformanceReportClick: (props: CoursePlanPerformanceReportClickProps) => {
    const { defArgs, sharedAppLinkParams } = props;
    const { navigateToApp } = defArgs.props;
    const action = AppAction.ASSIGNMENT_PERFORMANCE_VIEW;
    navigateToApp({
      app: Application.KNOWLEDGECARD,
      action,
      body: sharedAppLinkParams
    });
  },
  catalogPageTitleClick: (props: CatalogPageTitleClickProps) => {
    const { defArgs, contentItem } = props;
    const { navigateToApp } = defArgs.props;
    navigateToApp({
      app: Application.KNOWLEDGECARD,
      action: AppAction.ASSIGNMENT_PREVIEW,
      body: stripNilProps({
        catalogItem: contentItem,
        ref: RoutePath.CATALOG
      })
    });
  },
  assignmentOverviewTitleClick: (props: ApplicationNavigateHandlerMapProps) => navigateExternal(props),
  assignmentOverviewPerformanceReportClick: (props: ApplicationNavigateHandlerMapProps) => navigateExternal(props),
  navigateToAppForAssignmentPerformanceHandlerMap: () => Application.KNOWLEDGECARD,
  navigateToAppHandlerMap: () => Application.KNOWLEDGECARD,
  isScorable: true,
  assignmentInfoHandler: (props: GetAssignmentInfoProps): Promise<AssignmentDto> => Promise.resolve(props.newAssignment),
  getExternalEntity: (props: EntityProps) => props.externalEntity as CatalogAdaptiveLessonExternalEntityDto,
  getExternalEntityClone: (props: EntityProps) => props.externalEntity as CatalogAdaptiveLessonExternalEntityDto,
  sortCatalogItemsWithoutTaxonomy: (props: SortCatalogItems) => {
    const { contentItem, catalog } = props;
    const enhancedLesson = getEnhancedLesson(contentItem, catalog);
    if (!enhancedLesson) {
      return null;
    }
    return enhancedLesson.entity.attributes.displayOrder;
  },
  sortCatalogItemsWithoutTaxonomyClone: (props: SortCatalogItems) => {
    const { contentItem, externalEntitiesMap } = props;
    const enhancedLesson = getEnhancedLessonClone(contentItem, externalEntitiesMap);
    if (!enhancedLesson) {
      return null;
    }
    return enhancedLesson.entity.attributes.displayOrder;
  },
  recContentItemTitleHandler: (props: RecContentItemTitleHandlerProps) => {
    const enhancedLesson = getEnhancedLesson(props.contentItem, props.catalog);
    if (enhancedLesson) {
      return enhancedLesson.entity.attributes.text || enhancedLesson.entity.attributes.displayName;
    }
    return null;
  },
  recContentItemTitleHandlerClone: (props: RecContentItemTitleHandlerPropsClone) => {
    const enhancedLesson = getEnhancedLessonClone(props.contentItem, props.externalEntitiesMap);
    if (enhancedLesson) {
      return enhancedLesson.entity.attributes.text || enhancedLesson.entity.attributes.displayName;
    }
    return null;
  },
  config: {
    sortOrder: 10,
    recContentType: RecContentItemTypeDto.ADAPTIVE_LESSON,
    icon: 'lesson',
    displayName: 'Lesson',
    displayNamePlural: 'Lessons',
    iconPrefix: 'hmds',
    iconClass: 'lesson',
    title: 'Adaptive Lesson',
    assignmentTypes: [AssignmentType.ADAPTIVE_LESSON],
    syllabusItemAction: SyllabusItemAction.ADD_ADAPTIVE_LESSON,
    configType: SyllabusItemConfigType.STATIC,
    resourceCategory: ResourceCategory.CONTENT_RESOURCES
  } as SyllabusItemTypeConfig
};
